import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TextSnippetIcon from '@mui/icons-material/TextSnippetOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import AlertBox from '@/components/AlertBox';
import Button from '@/components/Button';
import GeneralLoading from '@/components/GeneralLoading';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import DropzoneFile from '@/sections/Fulfill/Inventory/dependencies/AddProduct/dependencies/DropzoneFile';

import type { TooltipProps } from '@mui/material/Tooltip';

import { IEditProfile } from '../interfaces';
import { formPerCountryFunc } from './utils';
import WithInvoiceForm from './WithInvoiceForm';

const WithInvoice = ({
  register,
  errors,
  control,
  submitForm,
  watch,
  setValue,
  shouldDisableEditing,
}: IEditProfile) => {
  const theme = useTheme();
  const { profile } = useSelector((state: RootState) => state.parcel);
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const hasFetchedCatalogs = useRef(false);
  const [cfdiUses, setCfdiUses] = useState<
    Array<{ Name: string; Value: string; Natural: boolean; Moral: boolean }>
  >([]);
  const [fiscalRegimens, setFiscalRegimens] = useState<
    Array<{ Name: string; Value: string; Natural: boolean; Moral: boolean }>
  >([]);
  const country = profile.data?.country;

  // Load catalogs on mount (same pattern as client-console but with useRef to prevent double call)
  useEffect(() => {
    if (country !== 'MEX' || hasFetchedCatalogs.current) {
      return;
    }

    hasFetchedCatalogs.current = true;

    const loadCatalogs = async () => {
      try {
        const http = initializeHttpProvider();
        const [cfdiResponse, fiscalResponse] = await Promise.all([
          http.catalogs.getCfdiUses(),
          http.catalogs.getFiscalRegimens(),
        ]);

        if (cfdiResponse.success && cfdiResponse.data?.items) {
          // Map from API response (lowercase) to client-console structure (capitalized)
          const mappedCfdi = cfdiResponse.data.items.map((item) => ({
            Name: item.name,
            Value: item.value,
            Natural: item.natural,
            Moral: item.moral,
          }));
          setCfdiUses(mappedCfdi);
        }

        if (fiscalResponse.success && fiscalResponse.data?.items) {
          // Map from API response (lowercase) to client-console structure (capitalized)
          const mappedRegimens = fiscalResponse.data.items.map((item) => ({
            Name: item.name,
            Value: item.value,
            Natural: item.natural,
            Moral: item.moral,
          }));
          setFiscalRegimens(mappedRegimens);
        }
      } catch (error) {
        console.error('Error al cargar los catálogos:', error);
      }
    };

    loadCatalogs();
  }, [country]);

  // Convertir archivo PDF a base64 cuando se selecciona
  useEffect(() => {
    const convertFileToBase64 = async () => {
      if (file && setValue) {
        setUploadingFile(true);
        try {
          const reader = new FileReader();

          reader.onload = () => {
            const base64String = reader.result as string;
            // Remover el prefijo "data:application/pdf;base64," para obtener solo el base64
            const base64Data = base64String.split(',')[1];

            setValue('company.proofTaxSituationFile', base64Data);
            setFileName(file.name); // Guardar nombre del archivo
            toast.success('Archivo cargado correctamente');
          };

          reader.onerror = () => {
            toast.error('Error al cargar el archivo');
            setFile(null);
          };

          reader.readAsDataURL(file);
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Error al procesar el archivo');
          setFile(null);
        } finally {
          setUploadingFile(false);
        }
      }
    };

    convertFileToBase64();
  }, [file, setValue]);

  // Inicializar con mensaje si ya existe un archivo cargado previamente
  useEffect(() => {
    if (profile.data?.company?.proofTaxSituationFile && !fileName) {
      setFileName('Constancia de situación fiscal');
    }
  }, [profile.data?.company?.proofTaxSituationFile, fileName]);

  // Get current company type from watch
  const companyType = watch?.().company?.companyType || '';

  // Handler para cambios en régimen fiscal - captura descripción automáticamente
  const handleFiscalRegimeChange = useCallback(
    (selectedValue: string) => {
      if (setValue) {
        setValue('company.taxRegime', selectedValue);

        // Buscar y guardar la descripción del régimen seleccionado (mismo patrón que client-console)
        const selectedRegime = fiscalRegimens.find((regime) => regime.Value === selectedValue);
        if (selectedRegime) {
          setValue('company.taxRegimeDescription', selectedRegime.Name);
        }
      }
    },
    [setValue, fiscalRegimens],
  );

  // Handler para cambios en CFDI - captura descripción automáticamente
  const handleCfdiChange = useCallback(
    (selectedValue: string) => {
      if (setValue) {
        setValue('company.cfdiUse', selectedValue);

        // Buscar y guardar la descripción del CFDI seleccionado (mismo patrón que client-console)
        const selectedCfdi = cfdiUses.find((cfdi) => cfdi.Value === selectedValue);
        if (selectedCfdi) {
          setValue('company.cfdiDescription', selectedCfdi.Name);
        }
      }
    },
    [setValue, cfdiUses],
  );

  // Pass all catalogs to form fields (filtering happens in WithInvoiceForm component)
  const formFields = useMemo(() => {
    const fields = formPerCountryFunc(country ?? 'MEX');
    return fields.map((field) => {
      if (field.name === 'company.cfdiUse') {
        return {
          ...field,
          catalogOptions: cfdiUses,
          companyType,
          onCustomChange: handleCfdiChange,
        };
      }
      if (field.name === 'company.taxRegime') {
        return {
          ...field,
          catalogOptions: fiscalRegimens,
          companyType,
          onCustomChange: handleFiscalRegimeChange,
        };
      }
      return field;
    });
  }, [country, cfdiUses, fiscalRegimens, companyType, handleCfdiChange, handleFiscalRegimeChange]);

  return (
    <Grid
      container
      spacing={2}
      direction="row"
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      {formFields.map((section, index) => {
        return (
          <WithInvoiceForm
            key={`${section.name}-${index}`}
            register={register}
            errors={errors}
            control={control}
            data={section}
            disabled={shouldDisableEditing}
            country={country}
          />
        );
      })}
      <Grid size={{ xxs: 12, md: 6 }}>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.secondary.main,
            fontSize: '1rem',
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
            <CreditCardOutlinedIcon />
          </Icon>
          Método de pago
        </Typography>
        <Typography variant="body1">
          {profile.data?.configuration.paymentType === 'PREPAID' ? 'Prepago' : 'Mensual'}
        </Typography>
      </Grid>

      {profile.data?.country === 'MEX' && (
        <>
          <Grid size={{ xxs: 12 }} display="flex" alignItems="center">
            <Typography variant="subtitle2" color={theme.palette.primary.main}>
              Adjunta la constancia a situación fiscal
            </Typography>
            <IconButton>
              <HtmlTooltip title={<Typography variant="body2">Ver más</Typography>}>
                <HelpOutlineIcon color="disabled" />
              </HtmlTooltip>
            </IconButton>
          </Grid>

          <AlertBox type="warning" direction="row">
            <Typography color={theme.palette.grey[500]} fontWeight="bold">
              Verifica que el archivo que subas sea la constancia de situación fiscal emitida por el
              SAT y que tenga una vigencia no mayor a 30 días para evitar inconvenientes en la
              generación de tu factura
            </Typography>
          </AlertBox>

          <Grid size={{ xxs: 12 }} display="flex" gap="0.5rem" alignItems="center">
            {watch && watch().company && (watch().company as any).proofTaxSituationFile ? (
              <>
                <Box>
                  <TextSnippetIcon color="primary" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Archivo cargado:
                  </Typography>
                  <Typography variant="body2">
                    {fileName || 'Constancia de situación fiscal'}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography
                variant="caption"
                color={theme.palette.secondary.main}
                fontWeight="bold"
                sx={{ mt: '1rem' }}
              >
                Por favor adjunta tu Constancia, no tenemos registro de alguna
              </Typography>
            )}
          </Grid>

          {!shouldDisableEditing && (
            <>
              {uploadingFile ? (
                <Grid size={{ xxs: 12 }}>
                  <GeneralLoading size={100} text="Subiendo archivo" withDots fontVariant="h6" />
                </Grid>
              ) : (
                <Grid size={{ xxs: 12 }}>
                  <DropzoneFile setFile={setFile} type="pdf" />
                </Grid>
              )}
            </>
          )}
        </>
      )}

      {!shouldDisableEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'end', width: '100%', marginTop: '1rem' }}>
          <Button
            onClick={() => submitForm?.('company')}
            disabled={Object.entries(errors.company! ?? {}).length > 0}
          >
            Actualizar datos
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export default WithInvoice;

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));
